// The command net meter (spec §5): how much of the last five sim minutes the headquarters spent talking,
// and how long a new order would wait for its turn on the net. Amber from 90% occupancy, where the deck
// starts showing the wait on every card.
//
// Pitch 2 ("time you can feel") added two readouts beside the trailing-window bar: the review found the
// bar reads 0% for most of a fight after the opening burst of orders (2026-09-21 review, item A.4) - true
// of the last five minutes, uninformative about the fight as a whole. "Orders this hour" and "longest
// wait" are computed here, client-side, from `snapshot.orders` over the last sim hour (600 ticks), so the
// meter still says something once the five-minute window has gone quiet.

import type { Order } from '../../sim/types';
import { TICKS_PER_MIN, TICK_SEC } from '../../sim/types';
import { Chip } from '../cards/ui';

/** Occupancy at or above this fraction counts as a saturated net. */
export const SATURATED = 0.9;

/** The "orders this hour" / "longest wait" window: the last sim hour, in ticks. */
export const HOUR_TICKS = 60 * TICKS_PER_MIN;

/** `mm:ss`, for the longest-wait readout - short enough that seconds are always worth showing. */
function fmtMmSs(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function NetMeter({
  occupancy, queueSec, orders, tick,
}: { occupancy: number; queueSec: number; orders: Order[]; tick: number }) {
  const pct = Math.max(0, Math.min(1, occupancy));
  const saturated = pct >= SATURATED;
  const color = saturated ? 'var(--color-warn)' : 'var(--color-friendly)';

  const windowStart = tick - HOUR_TICKS;
  let ordersThisHour = 0;
  let longestWaitTicks = 0;
  for (const o of orders) {
    if (o.side !== 0 || o.issuedAt < windowStart) continue;
    ordersThisHour++;
    const waitTicks = o.netStart - o.issuedAt;
    if (waitTicks > longestWaitTicks) longestWaitTicks = waitTicks;
  }
  const longestWaitSec = longestWaitTicks * TICK_SEC;

  return (
    <div data-testid="net-meter" className="flex min-w-0 flex-1 items-center gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <span className="mono shrink-0 text-[9px] uppercase tracking-[0.2em] text-fog">Command net</span>
        <span
          className="relative h-[6px] min-w-24 flex-1 overflow-hidden rounded-full"
          style={{ background: 'var(--color-slate-700)' }}
          title={`Command-net occupancy over the last five sim minutes: ${Math.round(pct * 100)}%`}
        >
          <span className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct * 100}%`, background: color }} />
          {/* The saturation mark sits where cards start showing their wait. */}
          <span className="absolute inset-y-0" style={{ left: `${SATURATED * 100}%`, width: 1, background: 'var(--color-fog)' }} />
        </span>
        <span className="mono shrink-0 text-[10px]" style={{ color }}>{Math.round(pct * 100)}%</span>
        <Chip tone={queueSec > 0 ? 'warn' : 'neutral'} active={queueSec > 0} testId="net-queue">
          queue +{Math.round(queueSec)}s
        </Chip>
      </div>
      {/* Persistent readouts (pitch 2): informative even once the trailing five-minute window is quiet. */}
      <span
        data-testid="net-orders-hour"
        className="mono shrink-0 text-[10px] text-fog"
        title="Orders your headquarters issued in the last sim hour"
      >
        {ordersThisHour} orders/hr
      </span>
      <span
        data-testid="net-longest-wait"
        className="mono shrink-0 text-[10px] text-fog"
        title="Longest an order sat queued for the net before transmitting, in the last sim hour"
      >
        longest wait {fmtMmSs(longestWaitSec)}
      </span>
    </div>
  );
}
